import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useTrackerDevices } from '../../hooks';

/**
 * Componente widget para mostrar dispositivos de tracking
 * @param {Object} props - Propiedades del componente
 * @param {string} props.userUid - ID del usuario
 * @param {string} props.title - Título del widget
 */
export const TrackerDevicesWidget = ({ userUid, title = "Dispositivos de Tracking" }) => {
  const {
    devices,
    loading,
    error,
    currentLocation,
    refreshDevices
  } = useTrackerDevices(userUid);

  const getDeviceStatusIcon = (device) => {
    if (device.status === 'online') {
      return <CheckCircleIcon color="success" />;
    } else if (device.status === 'offline') {
      return <ErrorIcon color="error" />;
    } else {
      return <WarningIcon color="warning" />;
    }
  };

  const getDeviceStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'error';
      default:
        return 'warning';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" action={
            <Tooltip title="Reintentar">
              <IconButton
                color="inherit"
                size="small"
                onClick={refreshDevices}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          }>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
          <Tooltip title="Actualizar dispositivos">
            <IconButton onClick={refreshDevices} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {currentLocation && (
          <Box mb={2}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Ubicación Actual
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <LocationIcon color="primary" fontSize="small" />
              <Typography variant="body2">
                {currentLocation.lat.toFixed(6)}, {currentLocation.lng.toFixed(6)}
              </Typography>
            </Box>
          </Box>
        )}

        {devices.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center">
            No se encontraron dispositivos de tracking
          </Typography>
        ) : (
          <List>
            {devices.map((device, index) => (
              <ListItem key={device.id || index} divider>
                <ListItemIcon>
                  {getDeviceStatusIcon(device)}
                </ListItemIcon>
                <ListItemText
                  primary={device.name || `Dispositivo ${index + 1}`}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {device.description || 'Sin descripción'}
                      </Typography>
                      {device.lastSeen && (
                        <Typography variant="caption" color="text.secondary">
                          Última actividad: {new Date(device.lastSeen).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                <Chip
                  label={device.status || 'desconocido'}
                  color={getDeviceStatusColor(device.status)}
                  size="small"
                  variant="outlined"
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
}; 